import React, { Fragment } from 'react';
import { CardContainer, CardContainerHeader, Expander, IconSwitcher, LegendItemList, Text, TextFontSize } from '@philips-internal/oswald-ui-components';
import { CardContainerBody, CardContainerSeparator, CardContainerZone, Field, FieldOrientation, Panel, TokenList } from '@philips-internal/oswald-ui-components/lib';
import { LayoutAlign, LayoutFlexDirection, LayoutJustify, Size, TextAlignment } from '@philips-internal/oswald-ui-constants';
import { AwesomeComponent, ComponentType } from '@philips-internal/oswald-ui-core';
import { MessageService, ModelService } from '@philips-internal/oswald-ui-core/lib/services';
import { generateUUID } from '@philips-internal/oswald-ui-utils';
import { InfusionsMedicinesCpoeCardAPI } from '.';
import { CpoeConstants, GroupItem, NutritionType } from './infusions-medicines-cpoe-card-types';
import './infusions-medicines-cpoe-cardcontainer.css';
import moment from 'moment';
var ColorLegend;
(function (ColorLegend) {
    ColorLegend["BLUE"] = "blue";
    ColorLegend["GRAY"] = "gray";
    ColorLegend["ORANGE"] = "orange";
    ColorLegend["RED"] = "red";
    ColorLegend["YELLOW"] = "yellow";
})(ColorLegend || (ColorLegend = {}));
const colorPontuation = {
    [ColorLegend.RED]: 0,
    [ColorLegend.YELLOW]: 1,
    [ColorLegend.BLUE]: 2,
    [ColorLegend.ORANGE]: 3,
    [ColorLegend.GRAY]: 4,
};
const awesomeProps = {
    api: InfusionsMedicinesCpoeCardAPI,
    type: ComponentType.DATA_DISPLAY,
};
function InfusionsMedicinesCpoeCardcontainer(props) {
    function renderHeader(legends, infusion, legendsColor) {
        const tokenListMaterial = legends?.flatMap(token => (token.item.match('material') || token.item === '' ? castLegendToToken(token) : []));
        const title = infusion.material || infusion.exam || infusion.blood || infusion.dialysis || infusion.diet || infusion.gastherapy || infusion.nursingInterventions || infusion.recommendation;
        return (React.createElement(CardContainerHeader, null,
            React.createElement(CardContainerZone, { flexDirection: LayoutFlexDirection.COLUMN, size: 100 },
                React.createElement(CardContainerZone, { flexDirection: LayoutFlexDirection.ROW, size: 100 },
                    React.createElement(CardContainerZone, { size: 85 },
                        React.createElement(Field, { entry: '', hideLabel: true, orientation: FieldOrientation.HORIZONTAL, strikethroughLabel: infusion?.suspended, strikethroughValue: infusion?.suspended, uuid: generateUUID(), width: 12 },
                            React.createElement(Text, { bold: true, size: TextFontSize.DEFAULT }, title))),
                    React.createElement(CardContainerZone, { size: 15 },
                        React.createElement(Fragment, null,
                            React.createElement(LegendItemList, { data: legendsColor, justifyContent: LayoutJustify.END })))),
                renderTokenListHeader(tokenListMaterial))));
    }
    function renderTokenListHeader(tokenListMaterial) {
        return (React.createElement(CardContainerZone, { flexDirection: LayoutFlexDirection.ROW, justifyContent: LayoutJustify.INITIAL, size: 100, uuid: generateUUID() },
            React.createElement(Panel, { uuid: generateUUID(), visible: tokenListMaterial?.length > 0 },
                React.createElement(TokenList, { collapsed: true, data: tokenListMaterial, noWrap: false }))));
    }
    function renderBody(infusion, legends) {
        const startDate = infusion.startDate ? ModelService.format('date', infusion.startDate, { type: 'timestamp' }) : '--';
        return (React.createElement(CardContainerBody, null,
            renderItems(infusion, legends),
            renderSubtitle(infusion),
            React.createElement(CardContainerZone, { size: 100 },
                React.createElement(Field, { entry: '', label: MessageService.translate('text.inicial'), orientation: FieldOrientation.HORIZONTAL, strikethroughLabel: infusion?.suspended, strikethroughValue: infusion?.suspended, uuid: generateUUID(), width: 12 },
                    React.createElement(Text, { size: getFontSizeBody() }, startDate))),
            renderMainContentBody(infusion)));
    }
    function renderItems(infusion, legends) {
        const displayDiluent = (infusion?.displayDiluent === CpoeConstants.YES);
        const displayMaterial = infusion?.displayMaterial ?? true;
        const materialDisplay = (flag) => displayMaterial && !!flag;
        return (React.createElement(Fragment, { key: infusion.id },
            infusion.diluent ? renderItem(infusion.diluent, infusion.doseDiluent, infusion.unitMeasurementDiluentDescription || infusion.unitMeasurementDiluent, legends, 'diluent', displayDiluent) : '',
            infusion.restroative ? renderItem(infusion.restroative, infusion.doseRestroative, infusion.unitMeasurementRestroativeDescription || infusion.unitMeasurementRestroative, legends, 'restroative', displayDiluent) : '',
            infusion.rediluent ? renderItem(infusion.rediluent, infusion.doseRediluent, infusion.unitMeasurementRediluentDescription || infusion.unitMeasurementRediluent, legends, 'rediluent', displayDiluent) : '',
            infusion.material1 ? renderItem(infusion.material1, infusion.dose1, infusion.unitMeasurementDescription1 || infusion.unitMeasurement1, legends, 'material1', materialDisplay(infusion?.displayMaterial1)) : '',
            infusion.material2 ? renderItem(infusion.material2, infusion.dose2, infusion.unitMeasurementDescription2 || infusion.unitMeasurement2, legends, 'material2', materialDisplay(infusion?.displayMaterial2)) : '',
            infusion.material3 ? renderItem(infusion.material3, infusion.dose3, infusion.unitMeasurementDescription3 || infusion.unitMeasurement3, legends, 'material3', materialDisplay(infusion?.displayMaterial3)) : '',
            infusion.material4 ? renderItem(infusion.material4, infusion.dose4, infusion.unitMeasurementDescription4 || infusion.unitMeasurement4, legends, 'material4', materialDisplay(infusion?.displayMaterial4)) : '',
            infusion.material5 ? renderItem(infusion.material5, infusion.dose5, infusion.unitMeasurementDescription5 || infusion.unitMeasurement5, legends, 'material5', materialDisplay(infusion?.displayMaterial5)) : '',
            infusion.material6 ? renderItem(infusion.material6, infusion.dose6, infusion.unitMeasurementDescription6 || infusion.unitMeasurement6, legends, 'material6', materialDisplay(infusion?.displayMaterial6)) : '',
            infusion.material7 ? renderItem(infusion.material7, infusion.dose7, infusion.unitMeasurementDescription7 || infusion.unitMeasurement7, legends, 'material7', materialDisplay(infusion?.displayMaterial7)) : ''));
    }
    function renderItem(mateiral, dose, unitMeasurement, legends, medicineType, display = true) {
        const doseAndUnit = `${getDoseFormatted(dose ?? '')} ${unitMeasurement}`;
        const legendsFilteredByItem = legends?.filter(legend => legend.item.match(medicineType));
        const tokenListItem = legendsFilteredByItem?.map(legend => (castLegendToToken(legend)));
        const { suspended } = props;
        return (display ?
            React.createElement(React.Fragment, null,
                React.createElement(Panel, { alignItems: LayoutAlign.CENTER, flexDirection: LayoutFlexDirection.ROW, justifyContent: LayoutJustify.SPACEBETWEEN, uuid: generateUUID() },
                    React.createElement(Field, { entry: '', hideLabel: true, strikethroughValue: suspended, uuid: generateUUID(), width: 3 },
                        React.createElement(Text, { size: getFontSizeBody() }, mateiral)),
                    React.createElement(Panel, { alignItems: LayoutAlign.CENTER, flexDirection: LayoutFlexDirection.ROW, justifyContent: LayoutJustify.END, margin: '0px 7px 0px 7px', uuid: generateUUID(), visible: tokenListItem?.length > 0 },
                        React.createElement(TokenList, { data: tokenListItem })),
                    React.createElement(Field, { entry: '', hideLabel: true, strikethroughValue: suspended, uuid: generateUUID(), width: 1 },
                        React.createElement(Text, { alignment: TextAlignment.RIGHT, bold: true, size: getFontSizeBody() }, doseAndUnit))),
                React.createElement(CardContainerSeparator, null))
            : React.createElement(React.Fragment, null));
    }
    function renderSubtitle(infusion) {
        return (React.createElement(CardContainerZone, { flexDirection: LayoutFlexDirection.ROW, size: 100 },
            React.createElement(Text, { size: getFontSizeBody(), strikethrough: infusion?.suspended }, getMedcicineDescription(infusion))));
    }
    function getMedcicineDescription(infusionsMedicinesCpoeCardProps) {
        return buildDescription([
            infusionsMedicinesCpoeCardProps.bloodProcedures,
            infusionsMedicinesCpoeCardProps.typeBlood,
            infusionsMedicinesCpoeCardProps.breathing,
            (getDoseFormatted(infusionsMedicinesCpoeCardProps.dose ?? '') || getDoseFormatted(infusionsMedicinesCpoeCardProps.doseDifferent ?? '')),
            (infusionsMedicinesCpoeCardProps.unitMeasurementDescription || infusionsMedicinesCpoeCardProps.unitMeasurement),
            (infusionsMedicinesCpoeCardProps.dialysisDescription?.split('   ')[1] ?? ''),
            infusionsMedicinesCpoeCardProps.doseRange,
            getApplicationRoute(infusionsMedicinesCpoeCardProps),
            (infusionsMedicinesCpoeCardProps.admistration?.trim() ? '' : infusionsMedicinesCpoeCardProps.period),
            infusionsMedicinesCpoeCardProps.descriptionDifferent,
            infusionsMedicinesCpoeCardProps.admistration,
            infusionsMedicinesCpoeCardProps.itemType,
            infusionsMedicinesCpoeCardProps.solutionType,
            (infusionsMedicinesCpoeCardProps.numberSteps && infusionsMedicinesCpoeCardProps.numberSteps + ' ' + MessageService.translate('text.steps-plural-optional')),
            infusionsMedicinesCpoeCardProps.days,
        ]);
    }
    function buildDescription(descriptions) {
        return descriptions
            .filter(item => item != null)
            .reduce((prev, curr) => {
            return `${prev} ${curr ?? ''}`;
        }, '');
    }
    function getApplicationRoute(infusionsMedicinesCpoeCardProps) {
        if (infusionsMedicinesCpoeCardProps.group === GroupItem.NUTRITION && infusionsMedicinesCpoeCardProps.typeDiet === NutritionType.MILK
            || infusionsMedicinesCpoeCardProps.group !== GroupItem.NUTRITION) {
            return infusionsMedicinesCpoeCardProps.applicationRoute ?? '';
        }
        return '';
    }
    function getDoseFormatted(dose) {
        const doseNumber = Number(dose);
        if (doseNumber) {
            const decimal = dose?.toString().split('.')[1];
            const doseFormatted = Number(decimal) > 0 ? doseNumber.toFixed(1).toLocaleLowerCase() : Math.trunc(doseNumber);
            return replaceDotToComma(doseFormatted?.toString());
        }
        return dose;
    }
    function replaceDotToComma(text) {
        const regex = /\./g;
        return text?.toString().replace(regex, ',');
    }
    function renderMainContentBody(infusion) {
        const endDate = infusion.endDate ? ModelService.format('date', infusion.endDate, { type: 'timestamp' }) : '--';
        const nextHour = infusion?.nextHour || '-';
        const hours = infusion?.hours?.split(' ');
        const hoursFormatted = infusion.group === GroupItem.GASTHERAPY ? infusion?.hours : formatHours(hours) ?? '--:--';
        return (React.createElement(Expander, { uuid: "expander", arrowVisible: hoursFormatted?.trim() !== '' },
            React.createElement(CardContainerZone, { "data-slot": "expander-content", flexDirection: LayoutFlexDirection.COLUMN, size: 59 },
                React.createElement(Field, { entry: '', label: MessageService.translate('text.final'), orientation: FieldOrientation.HORIZONTAL, strikethroughLabel: infusion?.suspended, strikethroughValue: infusion?.suspended, uuid: generateUUID(), width: 12 },
                    React.createElement(Text, { size: getFontSizeBody() }, endDate))),
            React.createElement(CardContainerZone, { "data-slot": "expander-content", flexDirection: LayoutFlexDirection.ROW, size: 31 },
                React.createElement(Field, { entry: '', width: 12, label: MessageService.translate('txt.next'), orientation: FieldOrientation.HORIZONTAL, strikethroughLabel: infusion?.suspended, strikethroughValue: infusion?.suspended, uuid: generateUUID() },
                    React.createElement(Text, { size: getFontSizeBody() }, nextHour))),
            renderHoursExpanderContent(hoursFormatted)));
    }
    function renderHoursExpanderContent(hours) {
        const iconClock = [{
                color: 'black',
                label: '',
                src: 'static/assets/clock.svg',
                value: 'clock'
            }];
        return (React.createElement(CardContainerZone, { "data-slot": "expander-expandable-content", alignItems: LayoutAlign.CENTER, flexDirection: LayoutFlexDirection.ROW, size: 100 },
            React.createElement(CardContainerZone, { uuid: generateUUID(), size: 9 },
                React.createElement(IconSwitcher, { fallbackIcon: iconClock[0], icons: iconClock, size: Size.MEDIUM, value: 'clock' })),
            React.createElement(CardContainerZone, { uuid: generateUUID(), size: 91 },
                React.createElement(Text, { size: getFontSizeBody() }, hours))));
    }
    function castLegend(legend, withoutText) {
        return { color: legend.color, label: legend.text, value: withoutText ? legend.color : legend.name };
    }
    function castLegendToToken(legend) {
        return { color: legend.color, enabled: true, value: legend.text };
    }
    function getColorFromSignalType(colorSignal) {
        if (colorSignal.match('signal')) {
            const colorSignalType = colorSignal;
            return colorSignalType.split('-')[1];
        }
        return colorSignal.split('-')[0];
    }
    const orderTokensByPriorty = (legendA, legendB) => {
        if (legendA?.color && legendB?.color) {
            const colorA = getColorFromSignalType(legendA.color);
            const colorB = getColorFromSignalType(legendB.color);
            const pontuationA = getPontuationColor(colorA);
            const pontuationB = getPontuationColor(colorB);
            return pontuationA - pontuationB;
        }
        return -1;
    };
    function getPontuationColor(color) {
        if (isColorLegend(color)) {
            return colorPontuation[color];
        }
        return 0;
    }
    function isColorLegend(color) {
        return Object.values(ColorLegend).includes(color);
    }
    function formatHours(hours) {
        if (hours?.length > 0) {
            hours = hours.map(item => (formatHour(item)));
        }
        return hours?.join(' ') ?? '';
    }
    function formatHour(hour) {
        if (hour.includes(':')) {
            return hour;
        }
        return moment().hour(Number(hour)).minute(0).format('LT');
    }
    function getFontSizeBody() {
        return TextFontSize.SMALL;
    }
    return renderCardContainer();
    function renderCardContainer() {
        const infusion = props;
        const legends = infusion.legendsCommon?.concat(infusion.legendsSpecific)?.sort(orderTokensByPriorty);
        const legendsSpecific = infusion.legendsSpecific?.length > 0 ? infusion.legendsSpecific.sort(orderTokensByPriorty) : [];
        const legendsColor = infusion.legendsTasy?.map(token => (castLegend(token, true)));
        return (React.createElement(CardContainer, { className: "cpoe-card", key: infusion.id, uuid: generateUUID(), width: 3 },
            renderHeader(legends, infusion, legendsColor),
            React.createElement(CardContainerSeparator, null),
            renderBody(infusion, legendsSpecific)));
    }
}
AwesomeComponent(awesomeProps)(InfusionsMedicinesCpoeCardcontainer);
export default InfusionsMedicinesCpoeCardcontainer;
//# sourceMappingURL=infusions-medicines-cpoe-cardcontainer.js.map